import React from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { teamService } from '../../services/teamService';
import { adminResources } from './resourceConfigs';
import ResourceManager from './components/ResourceManager';
import { QueryClientProvider } from '@tanstack/react-query';
import { adminQueryClient } from './queryClient';

const ADMIN_EMAIL_ALLOWLIST = ['admin@pitchlab.com', 'admin@earnmyedge.com'];

export default function AdminPage() {
  const { user } = useAuth();
  const [state, setState] = React.useState({ loading: true, allowed: false, error: null, resource: 'trainer_scenarios' });

  React.useEffect(() => {
    let mounted = true;
    const checkAccess = async () => {
      try {
        if (!user) {
          if (mounted) setState(s => ({ ...s, loading: false, allowed: false }));
          return;
        }

        // Email allowlist shortcut: grant access without team lookup
        const isAllowlisted = ADMIN_EMAIL_ALLOWLIST?.some(e => e?.toLowerCase() === (user?.email || '')?.toLowerCase());
        if (isAllowlisted) {
          if (mounted) setState(s => ({ ...s, loading: false, allowed: true }));
          return;
        }

        // Otherwise, check team roles
        const teams = await teamService.getUserTeams();
        const roles = teams?.flatMap(t => t?.members || [])?.filter(m => m?.user_id === user?.id || m?.userId === user?.id);
        const isAdmin = roles?.some(m => ['owner', 'manager'].includes(m?.role)) || false;
        if (mounted) setState(s => ({ ...s, loading: false, allowed: isAdmin }));
      } catch (err) {
        // On error, still grant access if allowlisted; otherwise show error and deny
        const isAllowlisted = ADMIN_EMAIL_ALLOWLIST?.some(e => e?.toLowerCase() === (user?.email || '')?.toLowerCase());
        if (mounted) setState(s => ({ ...s, loading: false, allowed: !!isAllowlisted, error: err.message || String(err) }));
      }
    };
    checkAccess();
    return () => { mounted = false; };
  }, [user]);

  if (!user) {
    return <div className="p-6">Please sign in to access admin.</div>;
  }
  if (state.loading) return <div className="p-6 text-gray-500">Checking access…</div>;
  if (!state.allowed) return <div className="p-6 text-red-600">Access denied. Admins only (team owner/manager).</div>;

  const resourceKeys = Object.keys(adminResources);
  const current = adminResources[state.resource];

  return (
    <QueryClientProvider client={adminQueryClient}>
      <div className="p-4 md:p-6 max-w-7xl mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Admin</h1>
            <p className="text-gray-500 text-sm">Manage content tables and import CSVs</p>
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          {resourceKeys.map((key) => (
            <button
              key={key}
              onClick={() => setState(s => ({ ...s, resource: key }))}
              className={`px-3 py-2 rounded border text-sm transition-colors ${
                state.resource === key 
                  ? 'bg-gray-900 text-white border-gray-900' 
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
              }`}
            >
              {adminResources[key].title}
            </button>
          ))}
        </div>

        <ResourceManager config={current} resourceKey={state.resource} />
      </div>
    </QueryClientProvider>
  );
}
